import React, { useState } from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  RadialBarChart, RadialBar, ResponsiveContainer,
  XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell
} from 'recharts';
import {
  Home, FileText, Calendar, Settings, Bell, User, ChevronRight,
  TrendingUp, Users, BarChart2, DollarSign, AlertCircle, CheckCircle,
  Clock, Star, Activity
} from 'lucide-react';

// ===================== DATA =====================
const progressData = [
  { month: 'Sep', score: 62 }, { month: 'Oct', score: 68 },
  { month: 'Nov', score: 72 }, { month: 'Dec', score: 75 },
  { month: 'Jan', score: 79 }, { month: 'Feb', score: 82 },
];
const strengthData = [
  { name: 'Critical Thinking', value: 92, color: '#2A9D8F' },
  { name: 'Emotional IQ', value: 84, color: '#8DA399' },
  { name: 'Analytical', value: 88, color: '#F4A261' },
  { name: 'Creativity', value: 72, color: '#2A9D8F' },
  { name: 'Adaptability', value: 79, color: '#8DA399' },
];
const counselorData = [
  { day: 'Mon', sessions: 3 }, { day: 'Tue', sessions: 5 },
  { day: 'Wed', sessions: 4 }, { day: 'Thu', sessions: 7 },
  { day: 'Fri', sessions: 6 }, { day: 'Sat', sessions: 2 },
];
const adminRevenue = [
  { month: 'Sep', revenue: 42000 }, { month: 'Oct', revenue: 58000 },
  { month: 'Nov', revenue: 52000 }, { month: 'Dec', revenue: 71000 },
  { month: 'Jan', revenue: 68000 }, { month: 'Feb', revenue: 89000 },
];
const radialData = [{ name: 'Score', value: 82, fill: '#2A9D8F' }];

// ===================== MINI COMPONENTS =====================
const StatCard = ({ icon, label, value, sub, color = '#2A9D8F' }) => (
  <div style={{
    background: 'rgba(255,255,255,0.7)', borderRadius: '16px', padding: '18px',
    border: '1px solid rgba(42,157,143,0.1)', flex: 1, minWidth: '120px',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
      <div style={{ color, width: '32px', height: '32px', borderRadius: '8px', background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</div>
    </div>
    <div style={{ fontFamily: 'Manrope', fontWeight: 800, fontSize: '22px', color: '#1A202C', lineHeight: 1 }}>{value}</div>
    <div style={{ fontSize: '11px', color: '#64748B', fontFamily: 'Inter', marginTop: '4px' }}>{label}</div>
    {sub && <div style={{ fontSize: '10px', color, fontWeight: 600, marginTop: '3px' }}>{sub}</div>}
  </div>
);

const SidebarItem = ({ icon, label, active }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 14px',
    borderRadius: '10px', marginBottom: '2px', cursor: 'pointer',
    background: active ? 'rgba(42,157,143,0.1)' : 'transparent',
    color: active ? '#2A9D8F' : '#64748B',
    transition: 'all 0.15s ease',
    borderLeft: active ? '2.5px solid #2A9D8F' : '2.5px solid transparent',
  }}>
    <span>{icon}</span>
    <span style={{ fontSize: '12px', fontFamily: 'Manrope', fontWeight: active ? 600 : 400 }}>{label}</span>
  </div>
);

const CustomTooltip = ({ active, payload, label, prefix = '' }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'rgba(255,255,255,0.95)', border: '1px solid rgba(42,157,143,0.2)',
      borderRadius: '10px', padding: '10px 14px', boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
      fontSize: '12px', fontFamily: 'Manrope',
    }}>
      <div style={{ color: '#64748B', marginBottom: '4px' }}>{label}</div>
      <div style={{ color: '#2A9D8F', fontWeight: 700 }}>{prefix}{payload[0].value}</div>
    </div>
  );
};

// ===================== TAB CONTENT =====================
const CustomerDash = () => (
  <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
    {/* Sidebar */}
    <div style={{ width: '168px', padding: '16px 10px', borderRight: '1px solid rgba(0,0,0,0.05)', flexShrink: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px 16px' }}>
        <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'linear-gradient(135deg, #2A9D8F, #8DA399)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '11px', fontWeight: 700 }}>A</div>
        <div>
          <div style={{ fontSize: '11px', fontFamily: 'Manrope', fontWeight: 700, color: '#1A202C' }}>Aryan K.</div>
          <div style={{ fontSize: '9px', color: '#8DA399' }}>Class 11</div>
        </div>
      </div>
      {[
        { icon: <Home size={13} />, label: 'Dashboard', active: true },
        { icon: <FileText size={13} />, label: 'Assessments', active: false },
        { icon: <Activity size={13} />, label: 'Reports', active: false },
        { icon: <Calendar size={13} />, label: 'Counseling', active: false },
        { icon: <User size={13} />, label: 'Profile', active: false },
        { icon: <Settings size={13} />, label: 'Settings', active: false },
      ].map(item => <SidebarItem key={item.label} {...item} />)}
    </div>

    {/* Main content */}
    <div style={{ flex: 1, padding: '16px 20px', overflow: 'auto', background: '#FAFAF8' }}>
      <div style={{ fontSize: '14px', fontFamily: 'Manrope', fontWeight: 700, color: '#1A202C', marginBottom: '16px' }}>
        Your Growth Overview
      </div>
      {/* Stats row */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <StatCard icon={<TrendingUp size={15} />} label="Overall Score" value="82" sub="+4 this month" />
        <StatCard icon={<CheckCircle size={15} />} label="Assessments" value="3/5" sub="2 remaining" color="#8DA399" />
        <StatCard icon={<Calendar size={15} />} label="Next Session" value="Feb 24" sub="10:00 AM" color="#F4A261" />
      </div>
      {/* Chart */}
      <div style={{ background: 'white', borderRadius: '14px', padding: '16px', border: '1px solid rgba(0,0,0,0.05)' }}>
        <div style={{ fontSize: '11px', fontFamily: 'Manrope', fontWeight: 600, color: '#64748B', marginBottom: '10px' }}>Progress Timeline</div>
        <ResponsiveContainer width="100%" height={110}>
          <AreaChart data={progressData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="dashGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#2A9D8F" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#2A9D8F" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
            <YAxis domain={[50, 100]} tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
            <Tooltip content={<CustomTooltip />} />
            <Area type="monotone" dataKey="score" stroke="#2A9D8F" strokeWidth={2} fill="url(#dashGrad)" dot={{ fill: '#2A9D8F', r: 3 }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {/* Strength bars */}
      <div style={{ background: 'white', borderRadius: '14px', padding: '14px', border: '1px solid rgba(0,0,0,0.05)', marginTop: '10px' }}>
        <div style={{ fontSize: '11px', fontFamily: 'Manrope', fontWeight: 600, color: '#64748B', marginBottom: '10px' }}>Strength Profile</div>
        {strengthData.slice(0, 3).map(s => (
          <div key={s.name} style={{ marginBottom: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
              <span style={{ fontSize: '10px', color: '#4A5568', fontFamily: 'Manrope', fontWeight: 500 }}>{s.name}</span>
              <span style={{ fontSize: '10px', color: s.color, fontWeight: 700 }}>{s.value}%</span>
            </div>
            <div style={{ height: '4px', background: '#F0F0EE', borderRadius: '99px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${s.value}%`, background: s.color, borderRadius: '99px' }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const CounselorDash = () => (
  <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
    <div style={{ width: '168px', padding: '16px 10px', borderRight: '1px solid rgba(0,0,0,0.05)', flexShrink: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px 16px' }}>
        <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'linear-gradient(135deg, #8DA399, #2A9D8F)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '11px', fontWeight: 700 }}>Dr</div>
        <div>
          <div style={{ fontSize: '11px', fontFamily: 'Manrope', fontWeight: 700, color: '#1A202C' }}>Dr. Priya S.</div>
          <div style={{ fontSize: '9px', color: '#8DA399' }}>Counselor</div>
        </div>
      </div>
      {[
        { icon: <Home size={13} />, label: 'Overview', active: true },
        { icon: <Users size={13} />, label: 'My Cases', active: false },
        { icon: <Calendar size={13} />, label: 'Schedule', active: false },
        { icon: <FileText size={13} />, label: 'Reports', active: false },
        { icon: <Settings size={13} />, label: 'Settings', active: false },
      ].map(item => <SidebarItem key={item.label} {...item} />)}
    </div>
    <div style={{ flex: 1, padding: '16px 20px', overflow: 'auto', background: '#FAFAF8' }}>
      <div style={{ fontSize: '14px', fontFamily: 'Manrope', fontWeight: 700, color: '#1A202C', marginBottom: '16px' }}>
        Counselor Overview
      </div>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <StatCard icon={<Users size={15} />} label="Active Cases" value="12" sub="2 new today" />
        <StatCard icon={<Calendar size={15} />} label="This Week" value="27" sub="Sessions" color="#8DA399" />
        <StatCard icon={<Star size={15} />} label="Rating" value="4.9" sub="Avg. feedback" color="#F4A261" />
      </div>
      <div style={{ background: 'white', borderRadius: '14px', padding: '16px', border: '1px solid rgba(0,0,0,0.05)' }}>
        <div style={{ fontSize: '11px', fontFamily: 'Manrope', fontWeight: 600, color: '#64748B', marginBottom: '10px' }}>Weekly Sessions</div>
        <ResponsiveContainer width="100%" height={120}>
          <BarChart data={counselorData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
            <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="sessions" fill="#2A9D8F" radius={[6, 6, 0, 0]} fillOpacity={0.85} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div style={{ background: 'white', borderRadius: '14px', padding: '14px', border: '1px solid rgba(0,0,0,0.05)', marginTop: '10px' }}>
        <div style={{ fontSize: '11px', fontFamily: 'Manrope', fontWeight: 600, color: '#64748B', marginBottom: '10px' }}>Recent Cases</div>
        {[
          { name: 'Aryan K.', status: 'Active', grade: 'Class 11', color: '#2A9D8F' },
          { name: 'Meera J.', status: 'Completed', grade: 'Class 10', color: '#8DA399' },
          { name: 'Rohan S.', status: 'Pending', grade: 'Class 12', color: '#F4A261' },
        ].map(c => (
          <div key={c.name} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: `${c.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, color: c.color }}>{c.name[0]}</div>
              <div>
                <div style={{ fontSize: '11px', fontFamily: 'Manrope', fontWeight: 600, color: '#1A202C' }}>{c.name}</div>
                <div style={{ fontSize: '9px', color: '#94A3B8' }}>{c.grade}</div>
              </div>
            </div>
            <span style={{ fontSize: '9px', fontFamily: 'Manrope', fontWeight: 700, color: c.color, background: `${c.color}15`, padding: '3px 8px', borderRadius: '99px' }}>{c.status}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const AdminDash = () => (
  <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
    <div style={{ width: '168px', padding: '16px 10px', borderRight: '1px solid rgba(0,0,0,0.05)', flexShrink: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px 16px' }}>
        <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'linear-gradient(135deg, #1A202C, #2A9D8F)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '11px', fontWeight: 700 }}>AD</div>
        <div>
          <div style={{ fontSize: '11px', fontFamily: 'Manrope', fontWeight: 700, color: '#1A202C' }}>Admin Panel</div>
          <div style={{ fontSize: '9px', color: '#8DA399' }}>Super Admin</div>
        </div>
      </div>
      {[
        { icon: <BarChart2 size={13} />, label: 'Analytics', active: true },
        { icon: <Users size={13} />, label: 'Users', active: false },
        { icon: <DollarSign size={13} />, label: 'Revenue', active: false },
        { icon: <FileText size={13} />, label: 'Plans', active: false },
        { icon: <AlertCircle size={13} />, label: 'Enquiries', active: false },
        { icon: <Settings size={13} />, label: 'Settings', active: false },
      ].map(item => <SidebarItem key={item.label} {...item} />)}
    </div>
    <div style={{ flex: 1, padding: '16px 20px', overflow: 'auto', background: '#FAFAF8' }}>
      <div style={{ fontSize: '14px', fontFamily: 'Manrope', fontWeight: 700, color: '#1A202C', marginBottom: '16px' }}>
        Platform Analytics
      </div>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <StatCard icon={<Users size={15} />} label="Total Users" value="2,411" sub="+124 this week" />
        <StatCard icon={<DollarSign size={15} />} label="Revenue (Feb)" value="₹89K" sub="+23% MoM" color="#8DA399" />
        <StatCard icon={<Activity size={15} />} label="Assessments" value="1,847" sub="This month" color="#F4A261" />
      </div>
      <div style={{ background: 'white', borderRadius: '14px', padding: '16px', border: '1px solid rgba(0,0,0,0.05)' }}>
        <div style={{ fontSize: '11px', fontFamily: 'Manrope', fontWeight: 600, color: '#64748B', marginBottom: '10px' }}>Monthly Revenue</div>
        <ResponsiveContainer width="100%" height={120}>
          <LineChart data={adminRevenue} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
            <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
            <Tooltip content={<CustomTooltip prefix="₹" />} />
            <Line type="monotone" dataKey="revenue" stroke="#2A9D8F" strokeWidth={2.5} dot={{ fill: '#2A9D8F', r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '10px' }}>
        {[
          { label: 'Active Counselors', value: '24', color: '#2A9D8F' },
          { label: 'Pending Requests', value: '7', color: '#F4A261' },
          { label: 'Plans Sold', value: '183', color: '#8DA399' },
          { label: 'Reports Generated', value: '427', color: '#2A9D8F' },
        ].map(m => (
          <div key={m.label} style={{
            background: 'white', borderRadius: '12px', padding: '12px 14px',
            border: '1px solid rgba(0,0,0,0.05)',
          }}>
            <div style={{ fontFamily: 'Manrope', fontWeight: 800, fontSize: '18px', color: m.color }}>{m.value}</div>
            <div style={{ fontSize: '10px', color: '#64748B', fontFamily: 'Inter', marginTop: '2px' }}>{m.label}</div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ===================== MAIN COMPONENT =====================
const DashboardPreview = () => {
  const [ref, visible] = useScrollReveal();
  const [activeTab, setActiveTab] = useState(0);
  const tabs = ['Customer', 'Counselor', 'Admin'];

  return (
    <section
      ref={ref}
      style={{ padding: '100px 24px', background: '#F9F9F7', position: 'relative' }}
      data-testid="dashboard-preview-section"
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div className={`reveal ${visible ? 'visible' : ''}`} style={{ textAlign: 'center', marginBottom: '56px' }}>
          <div className="section-label">Platform Dashboard</div>
          <div className="section-divider" />
          <h2 style={{
            fontFamily: 'Manrope', fontWeight: 700, fontSize: 'clamp(30px,3.8vw,46px)',
            color: '#1A202C', margin: '20px 0 16px', letterSpacing: '-0.02em',
          }}>
            Intelligent at Every <span style={{ color: '#2A9D8F' }}>Layer</span>
          </h2>
          <p style={{ fontSize: '16px', color: '#64748B', maxWidth: '500px', margin: '0 auto', lineHeight: 1.7 }}>
            A complete operational ecosystem — for students, counselors, and administrators alike.
          </p>
        </div>

        <div className={`reveal ${visible ? 'visible' : ''}`}>
          {/* Tab bar */}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginBottom: '24px' }}>
            {tabs.map((tab, i) => (
              <button
                key={tab}
                className={`dash-tab ${activeTab === i ? 'active' : ''}`}
                onClick={() => setActiveTab(i)}
                data-testid={`dashboard-tab-${tab.toLowerCase()}`}
              >
                {tab} Dashboard
              </button>
            ))}
          </div>

          {/* Dashboard frame */}
          <div style={{
            background: 'rgba(255,255,255,0.9)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(42,157,143,0.15)',
            borderRadius: '24px',
            overflow: 'hidden',
            boxShadow: '0 24px 80px rgba(42,157,143,0.12), 0 8px 24px rgba(0,0,0,0.06)',
            height: '480px',
            display: 'flex',
            flexDirection: 'column',
          }}>
            {/* Window chrome */}
            <div style={{
              padding: '14px 20px',
              background: 'rgba(249,249,247,0.9)',
              borderBottom: '1px solid rgba(0,0,0,0.06)',
              display: 'flex', alignItems: 'center', gap: '12px',
            }}>
              <div style={{ display: 'flex', gap: '6px' }}>
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#F4A261' }} />
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#8DA399' }} />
                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#2A9D8F' }} />
              </div>
              <div style={{
                flex: 1, height: '24px', background: 'rgba(0,0,0,0.04)',
                borderRadius: '6px', display: 'flex', alignItems: 'center', paddingLeft: '10px',
                fontSize: '10px', color: '#94A3B8', fontFamily: 'Inter',
              }}>
                app.themindvrik.com/dashboard
              </div>
              <Bell size={14} color="#94A3B8" />
            </div>

            {/* Dashboard content */}
            <div style={{ flex: 1, overflow: 'hidden', display: 'flex' }}>
              {activeTab === 0 && <CustomerDash />}
              {activeTab === 1 && <CounselorDash />}
              {activeTab === 2 && <AdminDash />}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DashboardPreview;
